import type { HistoryEventRecord } from '../../domain/types.js';
import { groupHistoryByTime } from '../worldRuntime/historyTree.js';

function label(key: string, mode: 'month' | 'week' | 'day' | 'hour'): string {
  if (mode === 'hour') return key.slice(-5);
  if (mode === 'month') {
    const [year, month] = key.split('-').map(Number);
    const date = new Date(year, (month || 1) - 1, 1);
    return Number.isFinite(date.getTime()) ? date.toLocaleDateString(undefined, { month: 'long', year: 'numeric' }) : key;
  }
  return key;
}

function localClock(iso: string): string {
  const date = new Date(iso);
  if (!Number.isFinite(date.getTime())) return iso;
  return date.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

export default function HistoryTreeView(props: {
  events: HistoryEventRecord[];
  /** Open every time group so each event is visible. Default keeps weeks collapsed. */
  expandAll?: boolean;
  /** Collapse every level including the month. Used by the missed-events panel only. */
  collapseAll?: boolean;
}): JSX.Element {
  const tree = groupHistoryByTime(props.events);
  const opened = props.expandAll ? { open: true as const } : {};
  const monthOpen = props.collapseAll ? {} : { open: true as const };
  if (tree.months.length === 0) return <p>No recorded activity yet.</p>;
  return (
    <div className="aquarium-history-tree">
      {tree.months.map((month) => (
        <details key={month.key} {...monthOpen}>
          <summary>{label(month.key, 'month')}</summary>
          {month.weeks.map((week) => (
            <details key={week.key} {...opened}>
              <summary>Week of {label(week.key, 'week')}</summary>
              {week.days.map((day) => (
                <details key={day.key} {...opened}>
                  <summary>{label(day.key, 'day')}</summary>
                  {day.hours.map((hour) => (
                    <details key={hour.key} {...opened}>
                      <summary>{label(hour.key, 'hour')}</summary>
                      <ul className="aquarium-history-list">
                        {hour.events.map((event) => (
                          <li key={event.eventId}>
                            <time dateTime={event.occurredAt}>{localClock(event.occurredAt)}</time>
                            <span>{event.summary}</span>
                          </li>
                        ))}
                      </ul>
                    </details>
                  ))}
                </details>
              ))}
            </details>
          ))}
        </details>
      ))}
    </div>
  );
}
